import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { UserX, Search, Send, Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import TemplatePicker from "@/components/TemplatePicker";
import SegmentMeter from "@/components/SegmentMeter";

export default function Inactive() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [openMsg, setOpenMsg] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/contacts/inactive");
      setContacts(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = contacts.filter(c =>
    !q ||
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(q.toLowerCase()) ||
    (c.phone || "").includes(q) ||
    (c.email || "").toLowerCase().includes(q.toLowerCase())
  );

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.id)));
  };

  const sendToSelected = async () => {
    if (!body.trim() || selected.size === 0) return;
    setSending(true);
    try {
      // Send individually via /messages/send loop (backend supports individual send)
      let count = 0;
      for (const cid of selected) {
        try {
          await api.post("/messages/send", { contact_id: cid, body });
          count++;
        } catch (e) { /* keep going */ }
      }
      toast.success(`Sent to ${count} of ${selected.size} inactive contacts`);
      setOpenMsg(false); setBody(""); setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="inactive-page">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <UserX className="w-7 h-7 text-slate-500" /> Inactive Contacts
            </h1>
            <p className="text-sm text-slate-500 mt-1">Parents with no active enrollments. Great for win-back campaigns or one-time follow-ups.</p>
          </div>
          <Button
            disabled={selected.size === 0}
            onClick={() => setOpenMsg(true)}
            className="bg-[#D9232E] hover:bg-[#B91C25] text-white disabled:opacity-50"
            data-testid="message-inactive-button"
          >
            <Send className="w-4 h-4 mr-2" /> Text {selected.size > 0 ? `${selected.size} Selected` : "Selected"}
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search inactive..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white border-slate-200" data-testid="inactive-search-input" />
          </div>
          <div className="text-xs text-slate-500">{contacts.length} inactive &middot; {selected.size} selected</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 w-10">
                  <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} data-testid="inactive-select-all" />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Last Dropped</th>
                <th className="text-left px-4 py-3 font-semibold">Tags</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody data-testid="inactive-table">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">No inactive contacts. Everyone has an active enrollment!</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inactive-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} data-testid={`inactive-check-${c.id}`} />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.first_name} {c.last_name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {c.last_dropped_at ? new Date(c.last_dropped_at).toLocaleDateString() : "—"}
                    {c.last_dropped_student && <div className="text-slate-400 mt-0.5">{c.last_dropped_student}</div>}
                    {c.last_dropped_class && <div className="text-slate-400 italic">from {c.last_dropped_class}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => nav("/inbox")} title="Open inbox" data-testid={`message-inactive-${c.id}`}>
                      <MessageSquareText className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={openMsg} onOpenChange={(v) => { setOpenMsg(v); if (!v) setBody(""); }}>
          <DialogContent data-testid="inactive-broadcast-dialog">
            <DialogHeader>
              <DialogTitle>Text {selected.size} inactive contacts</DialogTitle>
              <p className="text-sm text-slate-500">Sends an individual SMS to each — replies come back individually.</p>
            </DialogHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <SegmentMeter body={body} recipients={selected.size} variant="broadcast" onFix={setBody} testId="inactive-segment-meter" />
              <TemplatePicker onSelect={(b) => setBody(b)} testId="inactive-template-picker" />
            </div>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Hi! We miss you. Fall session enrollments are open now..." rows={5} data-testid="inactive-broadcast-body" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenMsg(false)}>Cancel</Button>
              <Button onClick={sendToSelected} disabled={sending || !body.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="inactive-broadcast-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send to ${selected.size}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
